import { useEffect } from 'react'
import { useErrorHandling } from '../errors'
import useAuthProvider from './useAuthProvider'
import useCheckAuth from './useCheckAuth'

const emptyParams = {}

/**
 * Restrict access to authenticated users.
 * Redirect anonymous users to the login page.
 *
 * Use it in your custom page components to require
 * authentication.
 *
 * You can set additional `authParams` at will if your authProvider
 * requires it.
 *
 * @example
 *     import { useAuthenticated } from 'react-admin';
 *     const FooPage = () => {
 *         useAuthenticated();
 *         return <Foo />;
 *     }
 *     const CustomRoutes = [
 *         <Route path="/foo" render={() => <FooPage />} />
 *     ];
 *     const App = () => (
 *         <Admin customRoutes={customRoutes}>
 *             ...
 *         </Admin>
 *     );
 */
export default (params: any = emptyParams) => {
  const authProvider = useAuthProvider()
  const checkAuth = useCheckAuth()
  const errorHandling = useErrorHandling()
  useEffect(() => {
    checkAuth(params).catch((error) => {
      // Skip the Sentry capture (added per #19283) when the user was never
      // logged in. AUTH_CHECK rejects with "Token not found" any time an
      // unauthenticated user opens a protected URL like /projects/<id>; that
      // is expected behaviour, not an error. The user is still redirected to
      // /login by useCheckAuth's logout() call regardless.
      if (!authProvider?.isLoggedIn()) return

      //@ts-ignore - Error constructor takes 1 argument in es5, 2 in es6, should work in most real cases
      const error2 = new Error('Check Auth Failed', { cause: error })
      errorHandling.unexpected(error2, {
        notify: false, // disable user facing notification
        console: false, // disable console.error
        captureContext: {
          tags: { reason: 'check-auth-failure' },
          extra: { logoutOnFailure: true, cause: error },
        },
      })
    })
  }, [checkAuth, params])
}
